import { Box } from '@chakra-ui/react';
import { usePrevious } from 'hooks/usePrevious';
import { CustomOnResize, CustomOnResizeEnd, CustomOnResizeStart } from 'modules/Editor/components/BoundingBox';
import { useBoundingBox } from 'modules/Editor/components/BoundingBox/useBoundingBox';
import { setActiveWidget, store, useAppDispatch, useAppSelector } from 'modules/Editor/store';
import { selectWidgets } from 'modules/Editor/store/infographSelector';
import { selectIsActiveWidget } from 'modules/Editor/store/widgetSelector';
import { useCallback, useLayoutEffect, useMemo, useRef } from 'react';
import { isLeftSideHandle, isSideHandle } from 'utils/boundingBox';
import { isTargetStyleNumber } from 'utils/dom';
import { ResponsiveWidgetToolbar, TransparentImg, WidgetBaseProp, useWidget } from 'widgets/sdk';

import { ReadOnlyResponsiveWidgetBase, ResponsiveWidgetBase } from '../ResponsiveWidgetBase';
import { getMaxTranslateX } from './StatChartWidget.helpers';
import { ComponentWidgetIdKeys, StatChartType, StatChartWidgetData } from './StatChartWidget.types';
import { StatChartWidgetToolbar, getSelectedWidgetType } from './toolbar';
import { CHART_TYPE_MAP } from './types';

export const StatChartWidget = ({ getWidgetMemberComponent }: WidgetBaseProp) => {
  const { componentWidgetIdMap, type, widgetId, options = { showLabel: true } } = useWidget<StatChartWidgetData>();
  const dispatch = useAppDispatch();

  const { boundingBox } = useBoundingBox();
  const maxResizeTranslateX = useRef<null | number>(null);

  const chartWidgetId = componentWidgetIdMap[ComponentWidgetIdKeys.Chart];
  const iconWidgetId = componentWidgetIdMap[ComponentWidgetIdKeys.IconGrid];
  const labelTextWidgetId = componentWidgetIdMap[ComponentWidgetIdKeys.MetricText];

  const isChartWidgetSelected = useAppSelector(selectIsActiveWidget(chartWidgetId));
  const isIconWidgetSelected = useAppSelector(selectIsActiveWidget(iconWidgetId));
  const isLabelTextWidgetSelected = useAppSelector(selectIsActiveWidget(labelTextWidgetId));
  const isMemberSelected = isChartWidgetSelected || isIconWidgetSelected || isLabelTextWidgetSelected;

  // For resetting the selection when type changes (e.g. from chart to icon)
  // Bounding box has to be completely reset
  const prevType = usePrevious(type);
  const isMemberSelectedRef = useRef(isMemberSelected);
  isMemberSelectedRef.current = isMemberSelected;

  const widgetIdRenderOrder = useMemo(() => {
    if (type === StatChartType.Icon) {
      return [
        componentWidgetIdMap[ComponentWidgetIdKeys.MetricText],
        componentWidgetIdMap[ComponentWidgetIdKeys.IconGrid],
      ];
    }

    return [
      componentWidgetIdMap[ComponentWidgetIdKeys.Chart],
      componentWidgetIdMap[ComponentWidgetIdKeys.MetricText],
      componentWidgetIdMap[ComponentWidgetIdKeys.IconGrid],
    ];
  }, [componentWidgetIdMap, type]);

  const fitWidthWidgets = useMemo(() => {
    if (type === StatChartType.ProgressBar) {
      return [componentWidgetIdMap[ComponentWidgetIdKeys.MetricText]];
    }

    return [];
  }, [componentWidgetIdMap, type]);

  const StatChartLayout = CHART_TYPE_MAP[type];

  const selectedWidgetType = getSelectedWidgetType({
    isChartWidgetSelected,
    isIconWidgetSelected,
    isLabelTextWidgetSelected,
  });

  // Update bounding box when the type changes
  // Need to reselect the widget if it was previously selected because the layout changed
  useLayoutEffect(() => {
    if (prevType && prevType !== type && isMemberSelectedRef.current) {
      dispatch(setActiveWidget({ widgetId, isInitial: true }));
    }
  }, [type, prevType, widgetId, dispatch]);

  const customOnResizeStart = useCallback(
    ({ event, onResizeStart }: CustomOnResizeStart) => {
      onResizeStart(event);

      // Set the max translateX for the left handle to prevent the widget from shifting when resizing from the left
      if (isLeftSideHandle(event.direction) && type === StatChartType.Icon) {
        const widgets = selectWidgets(store.getState());
        const statChartWidth = (event.target as HTMLElement).clientWidth;
        maxResizeTranslateX.current = getMaxTranslateX(widgetId, statChartWidth, widgets);
      }
    },
    [widgetId, type],
  );

  const customOnResizeEnd = useCallback(
    ({ onResizeEnd, event }: CustomOnResizeEnd) => {
      onResizeEnd(event);
      event.target.style.height = 'fit-content';
      maxResizeTranslateX.current = null;

      setTimeout(() => boundingBox.updateRect(), 0);
    },
    [boundingBox],
  );

  const customOnResize = useCallback(
    ({ event, frameMap }: CustomOnResize) => {
      // responsive widgets have side-handles - resize from side should not move the y-position of a widget
      const isSide = isSideHandle(event.direction);
      const target = event.target as HTMLElement;
      const frame = frameMap.get(event.target);
      const [newTranslateX, newTranslateY] = event.drag.beforeTranslate;

      // Ignore to update current widget style if value is a string(auto, fit-content, min-content, max-content, etc)
      if (isTargetStyleNumber(target.style.width)) target.style.width = `${event.width}px`;
      if (isTargetStyleNumber(target.style.height)) target.style.height = `${event.height}px`;

      // keep the right side fixed to the max X for the left handle when resizing from the left
      // Since the label is of fixed width, the right side should not move when resizing from the left
      if (isSide && type === StatChartType.Icon) {
        if (
          isLeftSideHandle(event.direction) &&
          typeof maxResizeTranslateX.current === 'number' &&
          newTranslateX > maxResizeTranslateX.current
        ) {
          target.style.transform = `translate(${maxResizeTranslateX.current}px, 0px) rotate(0deg)`;
          frame.translate = [maxResizeTranslateX.current, 0];
          return;
        }

        target.style.transform = `translate(${newTranslateX}px, 0px) rotate(0deg)`;
        frame.translate = [newTranslateX, 0];
      } else {
        target.style.transform = `translate(${newTranslateX}px, ${newTranslateY}px) rotate(0deg)`;
        frame.translate = event.drag.beforeTranslate;
      }
    },
    [type],
  );

  return (
    <ResponsiveWidgetBase
      fitWidthWidgets={fitWidthWidgets}
      isFitContentWidth={type === StatChartType.Icon}
      widgetIdRenderOrder={widgetIdRenderOrder}
      onResizeStart={customOnResizeStart}
      onResize={customOnResize}
      onResizeEnd={customOnResizeEnd}
    >
      <ResponsiveWidgetToolbar isActive={isMemberSelected}>
        {selectedWidgetType && (
          <StatChartWidgetToolbar selectedWidgetType={selectedWidgetType} showLabel={options?.showLabel} />
        )}
      </ResponsiveWidgetToolbar>
      <StatChartLayout getWidgetMemberComponent={getWidgetMemberComponent!} />
    </ResponsiveWidgetBase>
  );
};

export const ReadOnlyStatChartWidget = ({ getWidgetMemberComponent, includeAltTextImg }: WidgetBaseProp) => {
  const { type } = useWidget<StatChartWidgetData>();

  const StatChartLayout = CHART_TYPE_MAP[type];

  return (
    <ReadOnlyResponsiveWidgetBase>
      {includeAltTextImg && <TransparentImg />}
      {/* Member components should not be tagged in export */}
      <Box aria-hidden={true}>
        <StatChartLayout getWidgetMemberComponent={getWidgetMemberComponent!} isReadOnly />
      </Box>
    </ReadOnlyResponsiveWidgetBase>
  );
};
